import { InitHygenSessionPayload } from "@/store/modules/digital_human/interfaces";
import axios, { AxiosResponse } from "axios";
import { LiveAvatarSession } from "@heygen/liveavatar-web-sdk";
import { CREATE_LIVEAVATAR_SESSION_TOKEN } from "@/store/urls/hygen";

export interface LiveAvatarSessionToken {
  session_id: string;
  session_token: string;
}

const getApiKey = (): string => {
  const key = String(
    process.env.VUE_APP_LIVEAVATAR_API_KEY || process.env.VUE_APP_HYGEN_API || ""
  ).trim();
  return key;
};

const getApiErrorMessage = (error: unknown): string => {
  const axiosData = (error as { response?: { data?: { message?: string; code?: number } } })
    ?.response?.data;
  const message = axiosData?.message || (error as Error)?.message || "";
  if (axiosData?.code === 4001 || /invalid api key/i.test(message)) {
    return (
      "Invalid LiveAvatar API key. HeyGen keys do not work here — create a new key at " +
      "app.liveavatar.com/developers and set VUE_APP_LIVEAVATAR_API_KEY in your .env, then restart the dev server."
    );
  }
  return message || "Failed to create LiveAvatar session token";
};

const getApiUrl = (): string =>
  (
    process.env.VUE_APP_LIVEAVATAR_BASE_URL ||
    process.env.VUE_HYGEN_BASE_URL ||
    "https://api.liveavatar.com"
  ).replace(/\/$/, "");

export const create_liveavatar_session_token = async (
  payload: InitHygenSessionPayload
): Promise<LiveAvatarSessionToken> => {
  const avatarId = String(
    payload.avatar_id ||
      process.env.VUE_APP_LIVEAVATAR_AVATAR_ID ||
      process.env.VUE_APP_HYGEN_AVATAR_ID ||
      ""
  ).trim();

  if (!avatarId) {
    throw new Error(
      "VUE_APP_LIVEAVATAR_AVATAR_ID is required (UUID from app.liveavatar.com)"
    );
  }

  const apiKey = getApiKey();
  if (!apiKey) {
    throw new Error(
      "VUE_APP_LIVEAVATAR_API_KEY is not set. Get a key from app.liveavatar.com/developers"
    );
  }

  let response: AxiosResponse;
  try {
    response = await axios.post(
    CREATE_LIVEAVATAR_SESSION_TOKEN,
    {
      mode: "FULL",
      avatar_id: avatarId,
      avatar_persona: {
        ...(payload.voice?.voice_id
          ? { voice_id: payload.voice.voice_id }
          : {}),
        language: "en"
      },
      video_settings: {
        quality: payload.quality || "high"
      },
      ...(process.env.VUE_APP_LIVEAVATAR_SANDBOX === "true"
        ? { is_sandbox: true }
        : {})
    },
    {
      headers: {
        "Content-Type": "application/json",
        "X-API-KEY": apiKey
      }
    }
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }

  const data = response?.data?.data;
  if (!data?.session_id || !data?.session_token) {
    throw new Error(
      response?.data?.message || "Failed to create LiveAvatar session token"
    );
  }

  return data;
};

export const create_liveavatar_session = (
  sessionToken: string
): LiveAvatarSession => {
  return new LiveAvatarSession(sessionToken, { apiUrl: getApiUrl() });
};
